import * as Popover from "@radix-ui/react-popover";
import { Settings } from "lucide-react";
import { useNavigate } from "react-router";
import { cn } from "../../../utils/classes";
import { BellIcon } from "./bell-icon";
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotificationFeed,
  type Notification,
} from "./use-notifications";
import styles from "./notification-bell.module.css";

// Re-exported for `platform`'s own full-inbox page (`/notifications/all`,
// see that component's doc comment) — same feed/mutations the dropdown
// itself uses, so the two stay in sync via a shared react-query cache key
// instead of each maintaining its own fetch.
export {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotificationFeed,
  type Notification,
} from "./use-notifications";

export type NotificationBellProps = {
  /**
   * Unused — kept only so existing `<NotificationBell unreadCount={0} />`
   * call sites keep compiling. The badge count is now derived live from the
   * fetched notification feed (see `useNotificationFeed`), which always
   * wins over whatever is passed here.
   */
  unreadCount?: number;
  className?: string;
};

// Mirrors nazare-frontend's own `timeAgo()` in
// `app/web/app/components/notifications/notification-item.tsx`, adjusted
// for the live payload's epoch-ms `created_at` (see use-notifications.ts's
// `Notification` doc comment) rather than an ISO date string.
export function timeAgo(epochMs: number): string {
  const seconds = Math.floor((Date.now() - epochMs) / 1000);
  if (seconds < 60) return "just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

/**
 * `<TopHeader notifications={...}>` slot content: a bell icon button with a
 * live unread-count badge that opens a dropdown (`@radix-ui/react-popover`,
 * same dependency `<ThemeToggle>` uses) listing recent notifications.
 *
 * Wired to nazare-frontend's real `/notifications` endpoint (see
 * `use-notifications.ts`) via `@tanstack/react-query` — layout, copy, and
 * interactions (unread dot + tint, "Mark all read", per-item read-on-click,
 * relative timestamps) match `app/web/app/components/notifications/` as
 * closely as a polling client (no SSE worker) reasonably can. "View all
 * notifications" points at `/notifications/all` (a consumer app's own full
 * inbox page — `platform`'s lives at `platform/src/routes/notifications-all.tsx`)
 * rather than the bare `/notifications`, since at least one consumer
 * (`platform`) already uses that path for an unrelated admin tool. The
 * settings gear points at `/settings` — nazare-frontend's dedicated
 * `/settings/notifications` screen doesn't have a platform equivalent yet.
 */
export function NotificationBell({ className }: NotificationBellProps) {
  const navigate = useNavigate();
  const {
    notifications,
    unreadCount,
    hasUnread,
    isLoading,
    isError,
    isSessionExpired,
  } = useNotificationFeed();
  const { mutate: markRead } = useMarkNotificationRead();
  const { mutate: markAllRead } = useMarkAllNotificationsRead();

  const badgeLabel = unreadCount > 9 ? "9+" : String(unreadCount);

  const handleItemClick = (notification: Notification) => {
    if (notification.read_at == null) {
      markRead(notification.id);
    }
    if (!notification.url) return;
    if (notification.url.startsWith("/")) {
      navigate(notification.url);
    } else {
      window.open(notification.url, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button
          type="button"
          className={cn(styles.trigger, className)}
          aria-label={
            hasUnread ? `Notifications, ${unreadCount} unread` : "Notifications"
          }
          title="Notifications"
        >
          <BellIcon className={styles.icon} />
          {hasUnread && <span className={styles.badge}>{badgeLabel}</span>}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="end"
          sideOffset={8}
          className={styles.content}
        >
          <div className={styles.header}>
            <h3 className={styles.headerTitle}>
              Notifications
              {unreadCount > 0 && (
                <span className={styles.headerCount}>{unreadCount}</span>
              )}
            </h3>
            <div className={styles.headerActions}>
              {hasUnread && (
                <button
                  type="button"
                  className={styles.markAllRead}
                  onClick={() => markAllRead()}
                >
                  Mark all read
                </button>
              )}
              <a
                href="/settings"
                className={styles.settingsLink}
                title="Notification settings"
                aria-label="Notification settings"
              >
                <Settings size={14} />
              </a>
            </div>
          </div>

          <div className={styles.list}>
            {isLoading && notifications.length === 0 ? (
              <div className={styles.loading}>
                <span className={styles.spinner} aria-hidden="true" />
              </div>
            ) : isSessionExpired && notifications.length === 0 ? (
              <p className={styles.empty}>Sign in again to see notifications</p>
            ) : isError && notifications.length === 0 ? (
              <p className={styles.empty}>Couldn&rsquo;t load notifications</p>
            ) : notifications.length === 0 ? (
              <p className={styles.empty}>No notifications yet</p>
            ) : (
              notifications.map((notification) => {
                const isUnread = notification.read_at == null;
                return (
                  <button
                    key={notification.id}
                    type="button"
                    className={cn(styles.item, isUnread && styles.itemUnread)}
                    onClick={() => handleItemClick(notification)}
                  >
                    <span
                      className={cn(styles.dot, isUnread && styles.dotUnread)}
                      aria-hidden="true"
                    />
                    <span className={styles.itemBody}>
                      <span
                        className={cn(
                          styles.itemTitle,
                          isUnread && styles.itemTitleUnread,
                        )}
                      >
                        {notification.title}
                      </span>
                      {notification.body && (
                        <span className={styles.itemDesc}>{notification.body}</span>
                      )}
                      <span className={styles.itemTime}>
                        {timeAgo(notification.created_at)}
                      </span>
                    </span>
                  </button>
                );
              })
            )}
          </div>

          <a href="/notifications/all" className={styles.footer}>
            View all notifications
          </a>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
